import React, { useRef, memo, useContext, useState, useLayoutEffect } from 'react'
import PropTypes from 'prop-types'

import { LanguageContext } from '../../../context/LanguageContext'
import { Spinner, Icon, Typography } from '../index'
import { util } from '../../../util'

const SearchInput = ({
    absolute,
    className,
    clearSearchHandler,
    error,
    isFullWidth = false,
    isModified,
    label,
    loading,
    name,
    onChange,
    passClearStateFunc,
    pillListDisplay = value => value,
    pillList,
    pillListHandler,
    removeFilterList,
    searchList,
    showClearOption,
    size = 'md',
    value,
    viewOnly = false,
    ...rest
}) => {
    //RtL
    const { rtl } = useContext(LanguageContext)

    const refInput = useRef()
    const searchInputRef = useRef()
    const listRef = useRef()

    const focusInput = () => refInput.current.focus()
    const [showSearchList, setShowSearchList] = useState(false) // handles show/hide the search list
    const [searchInputValue, setSearchInputValue] = useState('')

    const clearState = () => {
        setSearchInputValue('')
    }

    !!passClearStateFunc && passClearStateFunc(clearState)

    useLayoutEffect(() => {
        if (searchInputRef.current && listRef.current && isFullWidth) {
            listRef.current.style.width = `${searchInputRef.current.offsetWidth}px`
        }
    }, [isFullWidth, showSearchList, searchInputRef, listRef])

    const addSearchedItem = (item, name) => {
        setShowSearchList(false)
        const { label, value } = item
        pillList ? setSearchInputValue('') : setSearchInputValue(label)
        const filterData = pillList
            ? typeof label === 'number'
                ? pillList.filter(item => item === label)
                : pillList.filter(item => item.toLowerCase().includes(label.toLowerCase()))
            : []
        filterData.length <= 0 && pillListHandler(value, name)
    }

    const changeHandler = e => {
        const { value } = e.target
        value && !showSearchList && setShowSearchList(true)
        setSearchInputValue(value)
        !!onChange && onChange(e, value)
    }

    const clearInputValue = () => {
        clearState()
        !!clearSearchHandler && clearSearchHandler()
    }

    const getSearchList = () => {
        const filterList = util.searchFromList(searchInputValue.trim(), searchList)

        if (loading) {
            return (
                <div ref={listRef} className={'search-input__list__loading'}>
                    <Spinner isInline={true} />
                </div>
            )
        }

        const listClassName = util.joinClasses(
            'search-input__list',
            !isFullWidth && size && size,
            filterList.length > 15 && 'enableScroll',
            absolute && 'absolute'
        )

        return (
            filterList.length > 0 &&
            searchInputValue && (
                <div ref={listRef} className={listClassName}>
                    {filterList.map((item, index) => (
                        <div
                            onClick={() => addSearchedItem(item, name)}
                            key={index}
                            className={'search-input__list__item'}
                        >
                            {item.label}
                        </div>
                    ))}
                </div>
            )
        )
    }

    const renderPillList = () => (
        <div className={'selected-list'}>
            {pillList
                ? pillList.length > 0 &&
                  pillList.map(item => {
                      return (
                          <div key={item} className={'selected-list__pill'}>
                              <span>{pillListDisplay(item)}</span>
                              <Icon
                                  size={'xxs'}
                                  onClick={() => removeFilterList(item, name)}
                                  className={`selected-list__cancel ${viewOnly && 'view-only'}`}
                                  renderIcon={util.CancelIcon}
                              />
                          </div>
                      )
                  })
                : null}
        </div>
    )

    const renderInputValue = () => {
        let inputValue = value || searchInputValue
        if (viewOnly && !inputValue && (!pillList || pillList.length === 0)) {
            inputValue = '-'
        }

        const inputClass = util.joinClasses(
            'input-search',
            inputValue && 'has-value',
            pillList && pillList.length === 0 && 'no-data',
            size,
            isModified && 'modified',
            viewOnly && 'view-only'
        )

        return (
            <div className={'search-input-field'}>
                <input
                    ref={refInput}
                    className={inputClass}
                    value={inputValue}
                    onChange={changeHandler}
                    name={name}
                    autoComplete={'new-password'}
                    // autocomplete={'off'}
                    readOnly={viewOnly}
                    {...rest}
                />
                <label className={util.joinClasses('label', rtl && 'rtl')} onClick={focusInput}>
                    {label}
                </label>
                {showClearOption && (
                    <span className={'search-clear'} onClick={clearInputValue}>
                        Clear
                    </span>
                )}
                <Icon
                    onClick={() => refInput.current.focus()}
                    pointer={true}
                    className={`search-icon ${rtl && 'rtl'} ${viewOnly && 'view-only'}`}
                    size={'sm'}
                    renderIcon={util.SearchIcon}
                />
            </div>
        )
    }

    return (
        <div
            className={util.joinClasses(
                'search-input',
                size,
                isFullWidth && 'is-full-width',
                className
            )}
            ref={searchInputRef}
        >
            <div
                className={`search-input__wrapper ${!!error && 'show-error'} ${
                    !!searchInputValue && 'show-border'
                } ${viewOnly && 'view-only'} ${isModified && 'modified'}`}
            >
                {renderInputValue()}
                {renderPillList()}
            </div>
            {showSearchList && getSearchList()}
            <Typography
                show={error && error.length > 0}
                text={error}
                display={'block'}
                size={'xs'}
                className={'search-error mt-2'}
                color={'danger'}
                fontType={'medium'}
            />
        </div>
    )
}

SearchInput.propTypes = {
    className: PropTypes.string,
    label: PropTypes.any,
    searchList: PropTypes.array,
    pillList: PropTypes.array,
    onChange: PropTypes.func,
    size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
    name: PropTypes.string,
    value: PropTypes.any,
    pillListHandler: PropTypes.func,
    removeFilterList: PropTypes.func,
    loading: PropTypes.bool,
    res: PropTypes.object,
}

export default memo(SearchInput)
